import React, { useState } from 'react';
import { Table, Status, Dropdown, Button, Modal, SelectLabel, ToggleLabel, Toggle, Counter, Card, ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { fetcher } from '@weezevent/weezjs-core';
import { useHistory } from 'react-router-dom';
import Style from './ticketOfficeEvent.module.css';

export default function TicketOfficeEventItem({ ticket_office, ticket_office_event, organization, setSelectAll, selected, setSelected, syncTicketOffice }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);

    const [openParams, setOpenParams] = useState(false);
    const [thisIsChecked, setThisIsChecked] = useState(false);

    React.useEffect(() => {
        selected.includes(ticket_office_event) ? setThisIsChecked(true) : setThisIsChecked(false);
    }, [selected, ticket_office_event]);

    const getColor = ticket_office_event => {
        const colors = {
            published: 'green',
            draft: 'orange',
            closed: 'red'
        };
        return colors[ticket_office_event.status];
    };

    const changeVisibility = React.useCallback(() => {
        if (!ticket_office_event.event_ticket_office_id) {
            let action = ticket_office_event.active ? 'disable_visibility' : 'enable_visibility';
            action = action.concat('_create');
            const data = {
                ticket_office_id: ticket_office.id,
                event_id: ticket_office_event.id
            };
            fetcher.post(`/ticket/organizations/${organization.id}/ticket_office/${action}`, { data }).then(data => {
                syncTicketOffice({
                    allow_cache: false,
                    cache: false
                });
            });
        } else {
            const action = ticket_office_event.active ? 'disable_multiple_visibility' : 'enable_multiple_visibility';
            fetcher
                .post(`/ticket/organizations/${organization.id}/ticket_office/${ticket_office.id}/${action}`, {
                    data: { pks: [ticket_office_event.event_ticket_office_id], events_id: [] }
                })
                .then(() => {
                    toast.success(t('ticket.ticket_offices.events.toast_success'));
                    syncTicketOffice({
                        allow_cache: false,
                        cache: false
                    });
                })
                .catch(e => {
                    toast.error(t('ticket.ticket_offices.events.toast_error'));
                    console.error(e);
                });
        }
    }, [ticket_office, organization, ticket_office_event, syncTicketOffice, toast]);

    const eventIsVisible = React.useMemo(() => {
        return ticket_office_event.active;
    }, [ticket_office_event]);

    return (
        <>
            {openParams && (
                <DisplayModal
                    openParams={openParams}
                    setOpenParams={setOpenParams}
                    organization={organization}
                    ticket_office={ticket_office}
                    syncTicketOffice={syncTicketOffice}
                    ticket_office_event={ticket_office_event}
                    pk={ticket_office_event.event_ticket_office_id}
                />
            )}
            <Table.Tr
                className={Style['event-item-table']}
                disabled={ticket_office_event.status === 'closed' ? true : false}
                onClick={() => history.push(`/ticket/O${organization.id}/ticket_offices/${ticket_office.id}/events/${ticket_office_event.id}`)}
                onSelected={(_, data) => {
                    let new_checked_event = [...selected];
                    if (selected.includes(ticket_office_event)) {
                        new_checked_event.splice(selected.indexOf(ticket_office_event), 1);
                    } else {
                        new_checked_event.push(ticket_office_event);
                    }
                    setSelected(new_checked_event);
                    setThisIsChecked(true);
                    if (thisIsChecked) {
                        setSelectAll(false);
                    }
                }}
                active={thisIsChecked}
            >
                <Table.Td>
                    {new Date(ticket_office_event.date).toLocaleDateString([], {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                    })}
                </Table.Td>
                <Table.Td>{ticket_office_event.id}</Table.Td>
                <Table.Td>
                    <div className={Style['display_event_name_status']}>
                        <Status color={getColor(ticket_office_event)} />
                        {ticket_office_event.name}
                    </div>
                </Table.Td>
                <Table.Td>{t('ticket.ticket_offices.events.rate', { count: ticket_office_event.rates.length })}</Table.Td>
                <Table.Td>{ticket_office_event?.global_quota_max && ticket_office_event?.global_quota_enabled ? ticket_office_event?.global_quota_max : '-'}</Table.Td>
                <Table.Td>
                    <div onClick={e => e.stopPropagation()}>
                        <Toggle checked={eventIsVisible} onChange={changeVisibility} />
                    </div>
                </Table.Td>
                <Table.CellDropdown>
                    <Dropdown.Item item={t('ticket.ticket_offices.events.edit_settings')} onClick={() => setOpenParams(true)} />
                </Table.CellDropdown>
            </Table.Tr>
        </>
    );
}

function DisplayModal({ openParams, setOpenParams, syncTicketOffice, organization, ticket_office, ticket_office_event, pk }) {
    const [counterValue, setCounterValue] = useState(isNaN(parseInt(ticket_office_event?.global_quota_max, 10)) ? 0 : parseInt(ticket_office_event?.global_quota_max, 10));
    const [displayQuota, setDisplayQuota] = useState(ticket_office_event?.global_quota_enabled);
    const [selectForm, setSelectForm] = useState(parseInt(ticket_office_event?.default_form_id) ?? null);
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const sendParams = React.useCallback(async () => {
        let ticketOfficeEventId = pk;
        const my_quota_set = displayQuota ? 1 : 0;
        const data = {
            id_form_default: selectForm,
            quota_set: my_quota_set,
            quota_max: counterValue
        };
        if (!ticketOfficeEventId) {
            const data = {
                ticket_office_id: ticket_office.id,
                event_id: ticket_office_event.id
            };
            await fetcher.post(`/ticket/organizations/${organization.id}/ticket_office/enable_visibility_create`, { data }).then(({ response }) => {
                ticketOfficeEventId = response.id_guichet_evenement;
            });
        }
        await fetcher
            .patch(`/ticket/organizations/${organization.id}/ticket_office/${ticket_office.id}/ticket_office_event/${ticketOfficeEventId}`, { data })
            .then(() => {
                toast.success(t(`ticket.ticket_offices.events.params.success`));
            })
            .catch(e => {
                toast.error(`${e.name} StatusCode: ${e.code}`);
            })
            .finally(() => {
                setOpenParams(false);
                syncTicketOffice();
            });
    }, [displayQuota, selectForm, counterValue, syncTicketOffice, organization, ticket_office]);

    const formOptions = React.useMemo(() => {
        const forms = ticket_office_event?.forms || [];
        const formOptions = forms.map(form => {
            return { text: form.name, value: form.weez_id };
        });
        formOptions.push({ text: t('ticket.ticket_office_rates.default_form'), value: 0 });
        formOptions.push({ text: t('ticket.ticket_office_rates.no_form'), value: -1 });
        return formOptions;
    }, [ticket_office_event]);

    return (
        <Modal
            size={'medium'}
            open={openParams}
            onClose={() => setOpenParams(false)}
            title={t(`ticket.ticket_offices.events.params.modal.title`)}
            subtitle={t(`ticket.ticket_offices.events.params.modal.subtitle`)}
            cancelButton={<Button onClick={() => setOpenParams(false)} label={t(`ticket.common.action.cancel`)} />}
            submitButton={<Button primary onClick={sendParams} label={t(`ticket.common.action.save`)} />}
        >
            <SelectLabel
                useNew
                label={t(`ticket.ticket_offices.events.params.modal.quota.select_title`)}
                placeholder={t(`ticket.ticket_offices.events.params.modal.quota.select_placeholder`)}
                options={formOptions}
                onChange={value => {
                    setSelectForm(value);
                }}
                value={selectForm}
            />
            <ToggleLabel
                disabled={false}
                checked={ticket_office_event?.global_quota_enabled}
                label={t(`ticket.ticket_offices.events.params.modal.quota.toggle`)}
                inlinePosition="left"
                onChange={() => setDisplayQuota(!displayQuota)}
            />
            {displayQuota && (
                <Card style={{ display: 'grid' }}>
                    <Card.Content>
                        <div style={{ paddingBottom: '10px' }}>{t(`ticket.ticket_offices.events.params.modal.counter_label`)}</div>
                        <Counter
                            style={{ width: '25%' }}
                            max={10000}
                            min={0}
                            onChange={(event, value) => setCounterValue(value)}
                            step={1}
                            value={counterValue}
                            displayErrorIcons={false}
                        />
                    </Card.Content>
                </Card>
            )}
        </Modal>
    );
}
